// ── BCHNostr — Identity switcher ──────────────────────────────────
//
// Lets the logged-in person act as their personal account or any Business
// Page they own, and create new Pages. Switching installs the selected
// identity's signer and reloads (see useCurrentUser.switchToBusiness), so
// the whole app — posts, DMs, and the per-pubkey BCH tip wallet — comes
// back up as that identity.

import { useMemo, useState, useEffect } from 'react';
import { Store, User, Plus, Check, ArrowLeftRight, ChevronDown } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import {
  listBusinessIdentities, businessesOwnedBy, type BusinessIdentity,
} from '../lib/identities';
import CreateBusinessModal from './CreateBusinessModal';

export default function IdentitySwitcher({ onNavigate }: { onNavigate?: () => void }) {
  const { user, switchToBusiness, switchToPersonal } = useAppContext();
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // Bumped whenever the local business registry may have changed (e.g. a Page
  // was just created this session, with no reload) so the list re-reads it.
  const [registryVersion, setRegistryVersion] = useState(0);

  // Re-read the registry whenever the switcher opens, or after creating a
  // Page — the registry is plain localStorage the CreateBusinessModal writes
  // synchronously, but the memo below is otherwise only keyed on `user`
  // (which doesn't change until a reload), so a Page created without a reload
  // would never appear.
  useEffect(() => {
    if (open) setRegistryVersion(v => v + 1);
  }, [open]);

  // Also refresh when a Page is created anywhere in the app (the modal fires
  // this after writing the registry) so the switcher reflects it immediately.
  useEffect(() => {
    const bump = () => setRegistryVersion(v => v + 1);
    window.addEventListener('bchnostr:business-created', bump);
    return () => window.removeEventListener('bchnostr:business-created', bump);
  }, []);

  // Pages this browser can act as. When we're on a business Page, its owner
  // is user.ownerPubkey; when we're personal, it's user.pubkey. Either way
  // we show the set owned by the operating personal account, plus any other
  // Pages held on this device (so nothing is ever stranded).
  const { owned, actingBusiness } = useMemo(() => {
    // registryVersion is a dependency so this recomputes when the local
    // registry changes without a `user` change.
    void registryVersion;
    if (!user) return { owned: [] as BusinessIdentity[], actingBusiness: null as BusinessIdentity | null };
    const ownerPk = user.isBusiness ? (user.ownerPubkey || '') : user.pubkey;
    const mine = ownerPk ? businessesOwnedBy(ownerPk) : [];
    // Fold in any other locally-held Pages not already listed.
    const seen = new Set(mine.map(b => b.pubkey));
    const extras = listBusinessIdentities().filter(b => !seen.has(b.pubkey));
    const all = [...mine, ...extras];
    const acting = user.isBusiness ? all.find(b => b.pubkey === user.pubkey) || null : null;
    return { owned: all, actingBusiness: acting };
  }, [user, registryVersion]);

  if (!user) return null;

  // Nothing to switch between and no Pages yet: a single lightweight
  // "create" affordance rather than a full switcher.
  if (owned.length === 0 && !user.isBusiness) {
    return (
      <>
        <button
          onClick={() => setShowCreate(true)}
          className="mx-4 mb-4 flex items-center gap-2 w-full px-3 py-2 rounded-xl border border-dashed border-primary/40 text-primary text-xs font-semibold hover:bg-primary/5 transition-colors"
        >
          <Store className="w-3.5 h-3.5" /> Create a Business Page
        </button>
        {showCreate && <CreateBusinessModal onClose={() => setShowCreate(false)} />}
      </>
    );
  }

  const handleSwitchBusiness = (pubkey: string) => {
    if (user.isBusiness && user.pubkey === pubkey) { setOpen(false); return; }
    onNavigate?.();
    switchToBusiness(pubkey);
  };

  const handleSwitchPersonal = () => {
    if (!user.isBusiness) { setOpen(false); return; }
    onNavigate?.();
    switchToPersonal();
  };

  return (
    <div className="mx-4 mb-4">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-xl bg-muted/50 border border-border text-xs font-semibold hover:border-primary/40 transition-colors"
      >
        <ArrowLeftRight className="w-3.5 h-3.5 text-primary shrink-0" />
        <span className="flex-1 min-w-0 text-left truncate">
          {user.isBusiness
            ? `Acting as ${actingBusiness?.name || 'a Business Page'}`
            : 'Acting as yourself'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-1.5 rounded-xl border border-border bg-popover overflow-hidden">
          {/* Personal */}
          <button
            onClick={handleSwitchPersonal}
            className="flex items-center gap-2.5 w-full px-3 py-2.5 text-left hover:bg-muted transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-primary" />
            </div>
            <span className="flex-1 min-w-0 text-xs font-semibold truncate">Personal account</span>
            {!user.isBusiness && <Check className="w-4 h-4 text-primary shrink-0" />}
          </button>

          {/* Business Pages */}
          {owned.map(b => (
            <button
              key={b.pubkey}
              onClick={() => handleSwitchBusiness(b.pubkey)}
              className="flex items-center gap-2.5 w-full px-3 py-2.5 text-left hover:bg-muted transition-colors border-t border-border"
            >
              {b.picture ? (
                <img src={b.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  <Store className="w-4 h-4 text-primary" />
                </div>
              )}
              <span className="flex-1 min-w-0 text-xs font-semibold truncate">{b.name || 'Business Page'}</span>
              {user.isBusiness && user.pubkey === b.pubkey && <Check className="w-4 h-4 text-primary shrink-0" />}
            </button>
          ))}

          {/* Create — only from the personal account (a Page can't own a Page) */}
          {!user.isBusiness && (
            <button
              onClick={() => { setOpen(false); setShowCreate(true); }}
              className="flex items-center gap-2.5 w-full px-3 py-2.5 text-left hover:bg-muted transition-colors border-t border-border text-primary"
            >
              <div className="w-7 h-7 rounded-full border border-dashed border-primary/50 flex items-center justify-center shrink-0">
                <Plus className="w-4 h-4" />
              </div>
              <span className="flex-1 text-xs font-semibold">Create a Business Page</span>
            </button>
          )}
        </div>
      )}

      {showCreate && <CreateBusinessModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
